import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";
import { smoothScrollTo } from "@/lib/lenis";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/c137c95c-c864-4200-a8d8-aee5a7faa3c6/images/472fddcb8285fa21163045202d4de154e8d7e5fe5359ee0567a9994613ff0b5c.jpeg";

const LINKS = [
  { label: "Manifesto", href: "#manifesto", testid: NAV.linkManifesto },
  { label: "Capabilities", href: "#capabilities", testid: NAV.linkCapabilities },
  { label: "Work", href: "#work", testid: NAV.linkWork },
  { label: "Contact", href: "#contact", testid: NAV.linkContact },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    if (open) {
      setOpen(false);
      setTimeout(() => smoothScrollTo(href), 320);
    } else {
      smoothScrollTo(href);
    }
  };

  return (
    <header
      data-testid={NAV.header}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/70 border-b border-border/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <a href="#" data-testid={NAV.logo} className="flex items-center gap-2.5 group">
          <img src={LOGO_URL} alt="Pulse Agentic" className="h-8 w-8 rounded-md" />
          <span className="font-display text-lg font-medium tracking-tight text-ink">
            Pulse Agentic
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => scrollTo(l.href)}
              className="text-sm text-muted-foreground hover:text-ink transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-brand transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <Button
          data-testid={NAV.ctaButton}
          onClick={() => scrollTo("#contact")}
          className="hidden md:inline-flex bg-ink text-white hover:bg-brand rounded-full px-6"
        >
          Start a conversation
        </Button>

        <button
          data-testid={NAV.mobileMenuToggle}
          className="md:hidden p-2 text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden overflow-hidden bg-white border-b border-border/60"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => scrollTo(l.href)}
                  className="text-left text-base text-ink py-1"
                >
                  {l.label}
                </button>
              ))}
              <Button
                onClick={() => scrollTo("#contact")}
                className="bg-ink text-white hover:bg-brand rounded-full mt-2"
              >
                Start a conversation
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
